import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Mail, Key, CheckCircle2, AlertCircle, Eye, EyeOff, 
  Send, Cloud, TestTube, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EMAIL_PROVIDERS = [
  { id: 'sendgrid', name: 'SendGrid', icon: Send },
  { id: 'resend', name: 'Resend', icon: Mail },
  { id: 'aws_ses', name: 'AWS SES', icon: Cloud },
];

const BuilderEmailSettingsPage = () => {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [testEmail, setTestEmail] = useState('');
  
  const [enabledProviders, setEnabledProviders] = useState([]);
  const [hasPlatformDefaults, setHasPlatformDefaults] = useState(false);
  const [platformDefaultProvider, setPlatformDefaultProvider] = useState('');
  
  const [settings, setSettings] = useState({
    provider: '',
    use_platform_defaults: true,
    sendgrid_api_key: '',
    resend_api_key: '',
    aws_ses_access_key: '',
    aws_ses_secret_key: '',
    aws_ses_region: 'us-east-1',
    from_email: '',
    from_name: '',
    is_verified: false,
  });

  useEffect(() => {
    loadSettings();
    setTestEmail(user?.email || '');
  }, [user]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEnabledProviders(data.enabled_providers || []);
        setHasPlatformDefaults(data.has_platform_defaults);
        setPlatformDefaultProvider(data.platform_default_provider || '');
        
        if (data.builder_settings) {
          setSettings({
            provider: data.builder_settings.provider || '',
            use_platform_defaults: data.builder_settings.use_platform_defaults ?? true,
            sendgrid_api_key: data.builder_settings.sendgrid_api_key || '',
            resend_api_key: data.builder_settings.resend_api_key || '',
            aws_ses_access_key: data.builder_settings.aws_ses_access_key || '',
            aws_ses_secret_key: data.builder_settings.aws_ses_secret_key || '',
            aws_ses_region: data.builder_settings.aws_ses_region || 'us-east-1',
            from_email: data.builder_settings.from_email || '',
            from_name: data.builder_settings.from_name || '',
            is_verified: data.builder_settings.is_verified || false,
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/email-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success('Email settings saved!');
        await loadSettings(); // Reload to get updated verification status
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/email-settings/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ test_email: testEmail }),
      });

      const result = await response.json();
      if (result.success) {
        toast.success(result.message);
        setSettings(prev => ({ ...prev, is_verified: true }));
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!hasRole(['super_admin', 'builder_admin'])) {
    return (
      <div className="p-4 md:p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Admin access required to configure email settings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (enabledProviders.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No email providers have been enabled by the platform administrator yet. 
            Contact support to enable email sending.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const selectedProvider = EMAIL_PROVIDERS.find(p => p.id === settings.provider);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-amber-600" />
            Email Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure email sending for demand letters and notifications
          </p>
        </div>
        <div className="flex gap-2">
          {settings.is_verified && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>
      </div>

      {/* Provider Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Email Provider</CardTitle>
          <CardDescription>
            Choose how to send emails from your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Provider</Label>
            <Select 
              value={settings.provider} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, provider: v, is_verified: false }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose an email provider" />
              </SelectTrigger>
              <SelectContent>
                {enabledProviders.map(providerId => {
                  const provider = EMAIL_PROVIDERS.find(p => p.id === providerId);
                  return provider ? (
                    <SelectItem key={providerId} value={providerId}>
                      <div className="flex items-center gap-2">
                        <provider.icon className="h-4 w-4" />
                        {provider.name}
                        {providerId === platformDefaultProvider && (
                          <Badge variant="outline" className="ml-2 text-xs">Platform Default</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ) : null;
                })}
              </SelectContent>
            </Select>
          </div>

          {hasPlatformDefaults && settings.provider && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Use Platform Credentials</p>
                <p className="text-xs text-muted-foreground">
                  Use the default credentials provided by the platform
                </p>
              </div>
              <Switch
                checked={settings.use_platform_defaults}
                onCheckedChange={(checked) => setSettings(prev => ({ 
                  ...prev, 
                  use_platform_defaults: checked,
                  is_verified: false
                }))}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Own Credentials (if not using platform defaults) */}
      {settings.provider && !settings.use_platform_defaults && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Credentials</CardTitle>
            <CardDescription>
              Enter your own {selectedProvider?.name} credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.provider === 'sendgrid' && (
              <div className="space-y-2">
                <Label>SendGrid API Key</Label>
                <div className="relative">
                  <Input
                    type={showKeys.sendgrid ? 'text' : 'password'}
                    value={settings.sendgrid_api_key}
                    onChange={(e) => setSettings(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                    placeholder="SG.xxxxxxxx"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => toggleShowKey('sendgrid')}
                  >
                    {showKeys.sendgrid ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {settings.provider === 'resend' && (
              <div className="space-y-2">
                <Label>Resend API Key</Label>
                <div className="relative">
                  <Input
                    type={showKeys.resend ? 'text' : 'password'}
                    value={settings.resend_api_key}
                    onChange={(e) => setSettings(prev => ({ ...prev, resend_api_key: e.target.value }))}
                    placeholder="re_xxxxxxxx"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => toggleShowKey('resend')}
                  >
                    {showKeys.resend ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {settings.provider === 'aws_ses' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Access Key ID</Label>
                  <Input
                    value={settings.aws_ses_access_key}
                    onChange={(e) => setSettings(prev => ({ ...prev, aws_ses_access_key: e.target.value }))}
                    placeholder="AKIAXXXXXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secret Access Key</Label>
                  <div className="relative">
                    <Input
                      type={showKeys.aws_secret ? 'text' : 'password'}
                      value={settings.aws_ses_secret_key}
                      onChange={(e) => setSettings(prev => ({ ...prev, aws_ses_secret_key: e.target.value }))}
                      placeholder="xxxxxxxx"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => toggleShowKey('aws_secret')}
                    >
                      {showKeys.aws_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>AWS Region</Label>
                  <Select 
                    value={settings.aws_ses_region} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, aws_ses_region: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                      <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                      <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                      <SelectItem value="ap-south-1">Asia Pacific (Mumbai)</SelectItem>
                      <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* From Email Settings */}
      {settings.provider && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Sender Details</CardTitle>
            <CardDescription>
              Customize how your emails appear to recipients
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input
                  type="email"
                  value={settings.from_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, from_email: e.target.value }))}
                  placeholder="noreply@yourcompany.com"
                />
                <p className="text-xs text-muted-foreground">
                  Must be verified with your email provider
                </p>
              </div>
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  value={settings.from_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))}
                  placeholder="Your Company Name"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Email */}
      {settings.provider && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Test Configuration</CardTitle>
            <CardDescription>
              Send a test email to verify your settings work
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={handleTest} 
                disabled={testing || !settings.provider}
                className="w-full sm:w-auto"
              >
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <TestTube className="h-4 w-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>
            {!settings.is_verified && settings.provider && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please test your configuration before using it for demand letters.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? 'Saving...' : 'Save Email Settings'}
        </Button>
      </div>
    </div>
  );
};

export default BuilderEmailSettingsPage;
